import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import AuthorAvatar from "../components/AuthorAvatar";
import { useAuth } from "../lib/auth";
import { GraduationCap, Clock, BookOpen, Play, CheckCircle, ArrowLeft, Sparkle, CaretRight } from "@phosphor-icons/react";

const LEVEL_META = {
  beginner: { label: "BEGINNER", color: "#00E676" },
  intermediate: { label: "INTERMEDIATE", color: "#22D3EE" },
  advanced: { label: "ADVANCED", color: "#FFAB00" },
};

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/courses/${slug}`);
    setCourse(data);
    if (user) {
      try {
        const { data: enrolls } = await api.get(`/courses/me/enrollments`);
        const mine = (enrolls || []).find((e) => e.course_slug === slug);
        setEnrollment(mine || null);
      } catch (_) {}
    }
  };

  useEffect(() => { load(); }, [slug, user?.id]); // eslint-disable-line

  const enroll = async () => {
    if (!user) { navigate("/login"); return; }
    setEnrolling(true);
    try {
      const { data } = await api.post(`/courses/${slug}/enroll`);
      setEnrollment(data);
    } catch (_) {}
    finally { setEnrolling(false); }
  };

  if (!course) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING COURSE...</div></AppShell>;

  const lvl = LEVEL_META[course.level] || { label: course.level.toUpperCase(), color: "#E60012" };
  const cover = course.cover_image ? `${process.env.REACT_APP_BACKEND_URL}${course.cover_image}` : "";
  const completed = new Set(enrollment?.completed_lessons || []);
  const totalLessons = course.lesson_count || 0;
  const completedCount = completed.size;
  const progress = totalLessons ? Math.round((completedCount / totalLessons) * 100) : 0;
  const firstUnfinished = course.modules.flatMap((m) => m.lessons).find((l) => !completed.has(l.id));
  const ready = totalLessons > 0;

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="course-detail-page">
        <div className="lg:col-span-8">
          <Link to="/courses" className="btn btn-ghost btn-sm mb-6" data-testid="course-back">
            <ArrowLeft size={12} weight="bold" /> All courses
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="label-mono px-2 py-1 border" style={{ color: lvl.color, borderColor: lvl.color }}>{lvl.label}</span>
            {(course.tags || []).map((t) => (
              <span key={t} className="label-mono">#{t.toUpperCase()}</span>
            ))}
          </div>

          <h1 className="font-display text-white mb-4" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1.02 }} data-testid="course-title">
            {course.title}
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed mb-8">{course.tagline}</p>

          {cover && (
            <div className="border border-[#27272A] mb-8 bg-black overflow-hidden">
              <img src={cover} alt="" className="w-full max-h-[420px] object-cover" />
            </div>
          )}

          {!ready ? (
            <div className="ai-panel p-6 mb-8" data-testid="course-generating-panel">
              <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#FFAB00" }}>
                <Sparkle size={11} weight="fill" /> COURSE GENERATING
              </div>
              <p className="text-zinc-100 leading-relaxed">
                {course.instructor} is composing this curriculum right now. Modules and lessons will appear
                here within a few minutes — refresh the page to see progress.
              </p>
            </div>
          ) : (
            <div className="ai-panel p-5 mb-8" data-testid="course-summary">
              <div className="label-mono mb-2" style={{ color: "#E60012" }}>WHAT YOU'LL LEARN</div>
              <p className="text-zinc-100 leading-relaxed">{course.summary}</p>
            </div>
          )}

          {/* Modules outline */}
          {ready && (
            <section data-testid="course-curriculum">
              <h2 className="font-display text-white text-2xl mb-4" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Curriculum</h2>
              <div className="space-y-3">
                {course.modules.map((m, mi) => (
                  <div key={m.id} className="card p-5" data-testid={`module-${mi}`}>
                    <div className="label-mono mb-2" style={{ color: lvl.color }}>MODULE {String(mi + 1).padStart(2, "0")}</div>
                    <h3 className="font-display text-white text-lg mb-1" style={{ fontWeight: 700 }}>{m.title}</h3>
                    {m.summary && <p className="text-sm text-zinc-400 mb-4">{m.summary}</p>}
                    <ul className="space-y-2">
                      {m.lessons.map((l) => {
                        const done = completed.has(l.id);
                        const url = enrollment ? `/courses/${slug}/lesson/${l.id}` : "#";
                        return (
                          <li key={l.id}>
                            <Link
                              to={url}
                              onClick={(e) => { if (!enrollment) { e.preventDefault(); enroll(); } }}
                              className="flex items-center gap-3 text-sm text-zinc-200 hover:text-white p-2 -mx-2 hover:bg-[#1A1A1D] transition-colors group"
                              data-testid={`lesson-link-${l.id}`}
                            >
                              {done ? (
                                <CheckCircle size={16} weight="fill" style={{ color: "#00E676" }} />
                              ) : (
                                <Play size={14} weight="regular" className="text-zinc-500 group-hover:text-white" />
                              )}
                              <span className="flex-1 truncate">{l.title}</span>
                              <span className="label-mono">{l.duration_min}m</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="card p-5 sticky top-5" data-testid="course-side-panel">
            <div className="flex items-center gap-3 mb-5">
              <AuthorAvatar name={course.instructor} avatarUrl={course.instructor_avatar ? `${process.env.REACT_APP_BACKEND_URL}${course.instructor_avatar}` : ""} size={36} bg={lvl.color} />
              <div className="min-w-0">
                <div className="label-mono mb-0.5">INSTRUCTOR</div>
                <div className="text-white font-medium text-sm">{course.instructor}</div>
              </div>
            </div>

            {ready && (
              <ul className="space-y-2 mb-5 text-sm">
                <li className="flex items-center justify-between text-zinc-300"><span className="label-mono">LEVEL</span><span style={{ color: lvl.color }}>{lvl.label}</span></li>
                <li className="flex items-center justify-between text-zinc-300"><span className="label-mono">LESSONS</span><span className="text-white">{course.lesson_count}</span></li>
                <li className="flex items-center justify-between text-zinc-300"><span className="label-mono">DURATION</span><span className="text-white">{course.duration_min} min</span></li>
                <li className="flex items-center justify-between text-zinc-300"><span className="label-mono">PRICE</span><span className="text-white">{course.price === 0 ? "FREE" : `$${course.price}`}</span></li>
                <li className="flex items-center justify-between text-zinc-300"><span className="label-mono">ENROLLED</span><span className="text-white">{course.enrollment_count}</span></li>
              </ul>
            )}

            {enrollment && firstUnfinished ? (
              <>
                <div className="mb-3">
                  <div className="flex justify-between mb-1.5"><span className="label-mono">PROGRESS</span><span className="text-white text-sm">{progress}%</span></div>
                  <div className="h-1.5 bg-[#1A1A1D] overflow-hidden">
                    <div className="h-full" style={{ width: `${progress}%`, background: "#00E676" }} />
                  </div>
                </div>
                <Link to={`/courses/${slug}/lesson/${firstUnfinished.id}`} className="btn btn-primary w-full" data-testid="course-continue-btn">
                  Continue learning <CaretRight size={12} weight="bold" />
                </Link>
              </>
            ) : enrollment ? (
              <div className="text-center py-2">
                <CheckCircle size={28} weight="fill" style={{ color: "#00E676" }} className="mx-auto mb-2" />
                <div className="text-white">Course complete!</div>
                <div className="label-mono">100% MASTERED</div>
              </div>
            ) : (
              <button onClick={enroll} disabled={enrolling || !ready} className="btn btn-primary w-full" data-testid="course-enroll-btn">
                {enrolling ? <span className="spinner mr-2" /> : <GraduationCap size={14} weight="fill" />}
                {ready ? (course.price === 0 ? "Enroll for free" : `Enroll · $${course.price}`) : "Generating..."}
              </button>
            )}
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
